import { lazy } from 'react'

import { ErrorBoundary } from 'react-error-boundary'
import { createBrowserRouter } from 'react-router'

import { AxiosInterceptor } from '@/app/axios-interceptor'
import { RootLayout } from '@/app/root-layout'
import {
  BuscarClientePageSuspense,
  CadastrarClientePageSuspense,
  ConfiguracoesReservaPageSuspense,
  ConsultaLeadsRepresadosPageSuspense,
  DetalhesEmpreendimentoPageSuspense,
  DetalhesLeadRepresadoPageSuspense,
  EspelhoDeVendasHorizontalPageSuspense,
  UltimosAtendimentosPageSuspense,
} from '@/app/routes/pages-suspense'
import { PrivateRoute } from '@/app/routes/private'
import { AuthProvider } from '@/features/user/providers'
import { EnvioDocumentosLeadsPage } from '@/pages/envio-documentos-leads'
import { EnvioDocumentosPropostaPage } from '@/pages/envio-documentos-proposta'
import { ErrorPage } from '@/pages/error/error'
import { PropostaPage } from '@/pages/proposta'
import PropostaListarProdutos from '@/pages/proposta-listar-produtos'
import { SimulacaoPage } from '@/pages/simulacao'
import { ventasApi } from '@/shared/lib'

const LoginPage = lazy(() => import('@/pages/login').then((module) => ({ default: module.LoginPage })))
const UltimosAtendimentosPage = lazy(() =>
  import('@/pages/ultimos-atendimentos').then((module) => ({ default: module.UltimosAtendimentosPage })),
)
const IniciarAtendimentoPage = lazy(() =>
  import('@/pages/iniciar-atendimento').then((module) => ({ default: module.IniciarAtendimentoPage })),
)
const CadastrarClienteAtendimentoPage = lazy(() =>
  import('@/pages/cadastrar-cliente-atendimento').then((module) => ({
    default: module.CadastrarClienteAtendimentoPage,
  })),
)
const SimuladorPage = lazy(() => import('@/pages/simulador').then((module) => ({ default: module.SimuladorPage })))
const SimuladorSalvarSimulacaoPage = lazy(() =>
  import('@/pages/simulador-salvar-simulacao').then((module) => ({ default: module.SimuladorSalvarSimulacaoPage })),
)
const HistoricoPage = lazy(() =>
  import('@/pages/historico/index').then((module) => ({ default: module.HistoricoSimulacaoPropostaPage })),
)
const ConsultaLeadsRepresadosPage = lazy(() =>
  import('@/pages/consulta-leads-represados').then((module) => ({ default: module.ConsultaLeadsRepresadosPage })),
)
const CadastroLeadRepresadoPage = lazy(() =>
  import('@/pages/cadastro-lead-represado').then((module) => ({ default: module.CadastroLeadRepresadoPage })),
)
const PropostaAtivaPage = lazy(() =>
  import('@/pages/proposta-ativa').then((module) => ({ default: module.PropostaAtivaPage })),
)
const EspelhoDeVendasPage = lazy(() =>
  import('@/pages/espelho-de-vendas').then((module) => ({ default: module.EspelhoDeVendasPage })),
)
const EspelhoDeVendasEmpreendimentoPage = lazy(() =>
  import('@/pages/espelho-de-vendas-empreendimento').then((module) => ({
    default: module.EspelhoDeVendasEmpreendimentoPage,
  })),
)
const EspelhoDeVendasHorizontalPage = lazy(() =>
  import('@/pages/espelho-de-vendas-horizontal').then((module) => ({
    default: module.EspelhoDeVendasHorizontalPage,
  })),
)
const EmpreendimentosPage = lazy(() =>
  import('@/pages/empreendimentos').then((module) => ({ default: module.EmpreendimentosPage })),
)
const DetalhesEmpreendimentoPage = lazy(() =>
  import('@/pages/detalhes-empreendimento/detalhes-empreendimento').then((module) => ({
    default: module.DetalhesEmpreendimentoPage,
  })),
)
const EmpreendimentoMidiasPage = lazy(() =>
  import('@/pages/empreendimento-midias').then((module) => ({
    default: module.EmpreendimentoMidiasPage,
  })),
)
const NotFoundPage = lazy(() =>
  import('@/pages/not-found').then((module) => ({
    default: module.NotFoundPage,
  })),
)
const PropostaDocumentosPage = lazy(() =>
  import('@/pages/proposta-documentos').then((module) => ({ default: module.PropostaDocumentosPage })),
)
const DetalhesLeadRepresadoPage = lazy(() =>
  import('@/pages/detalhes-lead-represado').then((module) => ({ default: module.DetalhesLeadRepresadoPage })),
)
const ConfiguracoesPerfilPage = lazy(() =>
  import('@/pages/configuracoes/perfil').then((module) => ({ default: module.ConfiguracoesPerfilPage })),
)
const ConfiguracoesReservaPage = lazy(() =>
  import('@/pages/configuracoes/reserva').then((module) => ({ default: module.ConfiguracoesReservaPage })),
)
const ConfiguracoesTermosPage = lazy(() =>
  import('@/pages/configuracoes/termos').then((module) => ({ default: module.ConfiguracoesTermosPage })),
)

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ErrorBoundary fallbackRender={ErrorPage}>
        <AuthProvider>
          <RootLayout />
        </AuthProvider>
      </ErrorBoundary>
    ),
    children: [
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: '',
        element: (
          <AxiosInterceptor api={ventasApi}>
            <PrivateRoute />
          </AxiosInterceptor>
        ),
        children: [
          {
            index: true,
            element: (
              <UltimosAtendimentosPageSuspense>
                <UltimosAtendimentosPage />
              </UltimosAtendimentosPageSuspense>
            ),
          },
          {
            path: 'leads-represados',
            children: [
              {
                index: true,
                element: (
                  <ConsultaLeadsRepresadosPageSuspense>
                    <ConsultaLeadsRepresadosPage />
                  </ConsultaLeadsRepresadosPageSuspense>
                ),
              },
              {
                path: 'cadastrar',
                element: <CadastroLeadRepresadoPage />,
              },
              {
                path: ':id',
                element: (
                  <DetalhesLeadRepresadoPageSuspense>
                    <DetalhesLeadRepresadoPage />
                  </DetalhesLeadRepresadoPageSuspense>
                ),
              },
              {
                path: ':id/enviar-documentos',
                element: <EnvioDocumentosLeadsPage />,
              },
            ],
          },
          {
            path: 'espelho-de-vendas',
            children: [
              {
                index: true,
                element: <EspelhoDeVendasPage />,
              },
              {
                path: ':idExternoEmpreendimento',
                element: <EspelhoDeVendasEmpreendimentoPage />,
              },
              {
                path: 'horizontal/:idExternoEmpreendimento',
                element: (
                  <EspelhoDeVendasHorizontalPageSuspense>
                    <EspelhoDeVendasHorizontalPage />
                  </EspelhoDeVendasHorizontalPageSuspense>
                ),
              },
            ],
          },
          {
            path: 'empreendimentos',
            children: [
              {
                index: true,
                element: <EmpreendimentosPage />,
              },
              {
                path: ':idExterno',
                element: (
                  <DetalhesEmpreendimentoPageSuspense>
                    <DetalhesEmpreendimentoPage />
                  </DetalhesEmpreendimentoPageSuspense>
                ),
              },
              {
                path: ':idExterno/:midiaSelecionada',
                element: <EmpreendimentoMidiasPage />,
              },
            ],
          },
          {
            path: 'atendimento',
            children: [
              {
                path: 'iniciar',
                element: (
                  <BuscarClientePageSuspense>
                    <IniciarAtendimentoPage />
                  </BuscarClientePageSuspense>
                ),
              },
              {
                path: 'cadastrar',
                element: (
                  <CadastrarClientePageSuspense>
                    <CadastrarClienteAtendimentoPage />
                  </CadastrarClientePageSuspense>
                ),
              },
            ],
          },
          {
            path: 'simulacao/:id',
            element: <SimulacaoPage />,
          },
          {
            path: 'simulador',
            children: [
              {
                index: true,
                element: <SimuladorPage />,
              },
              {
                path: ':salvar-simulacao',
                element: (
                  <BuscarClientePageSuspense>
                    <SimuladorSalvarSimulacaoPage />
                  </BuscarClientePageSuspense>
                ),
              },
            ],
          },
          {
            path: 'proposta',
            children: [
              {
                index: true,
                element: <PropostaPage />,
              },
              {
                path: 'selecao-produtos',
                element: <PropostaListarProdutos />,
              },
              {
                path: ':numero',
                element: <PropostaAtivaPage />,
              },
              {
                path: ':numero/documentos',
                element: <PropostaDocumentosPage />,
              },
              {
                path: ':numero/documentos/envio/:pessoaId',
                element: <EnvioDocumentosPropostaPage />,
              },
            ],
          },
          {
            path: 'historico/simulacoes',
            element: <HistoricoPage />,
          },
          {
            path: 'historico/propostas',
            element: <HistoricoPage />,
          },
          {
            path: 'configuracoes',
            children: [
              {
                path: 'reserva',
                element: (
                  <ConfiguracoesReservaPageSuspense>
                    <ConfiguracoesReservaPage />
                  </ConfiguracoesReservaPageSuspense>
                ),
              },
              {
                path: 'perfil',
                element: <ConfiguracoesPerfilPage />,
              },
              {
                path: 'termos',
                element: <ConfiguracoesTermosPage />,
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
